import { Injectable } from '@angular/core'
import { from, Observable, of } from 'rxjs'
import { flatMap, map } from 'rxjs/operators'
import { VerticalService } from 'src/app/singleton-services/vertical.service'

interface GoogleLoginSuccess {
  tag: 'success'
  token: string
}

interface GoogleLoginFailure {
  tag: 'failure'
  error: GoogleAuthError
}

export interface GoogleAuthError {
  code: string
  displayText1: string
  displayText2: string
}

export type GoogleLoginResult = GoogleLoginSuccess | GoogleLoginFailure

@Injectable()
export class GoogleAuthService {
  constructor(private readonly vs: VerticalService) {}

  /**
   * @Deprecated
   *
   * TODO: clean up and remove. Migrated to new google auth
   */
  public accessToken$: Observable<GoogleLoginResult | undefined> = this.vs.siteSettings$.pipe(
    flatMap(settings => {
      return settings.google_client_id ? from(this.initGoogle(settings.google_client_id)) : of(undefined)
    })
  )

  /**
   * Get google auth key from core API
   */
  public googleAuthKey$ = this.vs.siteSettings$.pipe(map(settings => settings.google_client_id))

  private async initGoogle(clientId: string | undefined): Promise<GoogleLoginResult | undefined> {
    return new Promise(res => {
      gapi.load('client', async () => {
        let error: GoogleAuthError | undefined

        const authInstance = await this.getGapi(clientId).catch(errorData => {
          error = this.getGoogleAuthErrorByCode(errorData.error)
        })
        if (error) {
          res({
            tag: 'failure',
            error
          })
          return
        }

        // Invalid Client ID.
        // Could happen if Core API is down or missing information?
        if (!authInstance) {
          res(undefined)
          return
        }

        // User is already signed in to a google account and is trying to change accounts
        const user = authInstance.currentUser.get()
        if (user && user.isSignedIn()) {
          await authInstance.signOut()
        }

        // Open the popup, wait for user to complete sign-in to Google.
        const googleUser = await authInstance
          .signIn({
            scope: 'openid email'
          })
          .catch(resp => {
            error = this.getGoogleAuthErrorByCode(resp.error)
          })
        if (error) {
          res({
            tag: 'failure',
            error
          })
          return
        }

        // User aborted sign-in voluntarily
        if (!googleUser) {
          res(undefined)
          return
        }

        res({
          tag: 'success',
          token: googleUser.getAuthResponse().id_token
        })
      })
    })
  }

  // Gapi doesnt play well with promises out of the box
  // see https://stackoverflow.com/questions/54299128/type-is-referenced-directly-or-indirectly-in-the-fulfillment-callback-of-its-own
  async getGapi(clientId: string | undefined) {
    return new Promise<Omit<gapi.auth2.GoogleAuth, 'then'>>(resolve => {
      resolve(
        gapi.auth2.init({
          client_id: clientId,
          scope: 'openid email'
        })
      )
    })
  }

  private getGoogleAuthErrorByCode(errorCode: string): GoogleAuthError | undefined {
    return [
      {
        code: 'popup_blocked_by_browser',
        displayText1: 'Your browser blocked the "Sign in with Google" popup.',
        displayText2: 'Please try again.'
      },
      {
        code: 'idpiframe_initialization_failed',
        displayText1: 'Your browser has disabled third-party cookies and cannot',
        displayText2: '"Sign in with Google." Please enable and try again.'
      }
    ].find(definedError => definedError.code === errorCode)
  }
}
